import { cn, formatMoney, pluralise } from '../../lib/format.js';

/**
 * The money. Shown in the bag, in checkout, and on the confirmation, always in
 * the same order so a shopper can compare the three at a glance and see that
 * nothing changed between them.
 *
 * Tax is reported as included rather than added, matching Indian retail
 * convention and the server's calculation. Showing it as a separate addition
 * would make the total look wrong.
 */

export const OrderSummary = ({ pricing, coupon, shipping, itemCount, className, children }) => {
  if (!pricing) return null;

  const savings = coupon?.discount ?? 0;

  return (
    <div className={cn('text-sm', className)}>
      <dl className="space-y-2.5">
        <Row label={itemCount ? `Subtotal (${pluralise(itemCount, 'item')})` : 'Subtotal'} value={formatMoney(pricing.subtotal)} />

        {savings > 0 && (
          <Row
            label={coupon?.code ? `Discount (${coupon.code})` : 'Discount'}
            value={`− ${formatMoney(savings)}`}
            tone="success"
          />
        )}

        <Row
          label="Delivery"
          value={pricing.shipping === 0 ? 'Free' : formatMoney(pricing.shipping)}
          tone={pricing.shipping === 0 ? 'success' : undefined}
        />

        {pricing.tax > 0 && (
          <Row
            label={
              pricing.taxInclusive
                ? `GST ${pricing.taxRate}% (included)`
                : `GST ${pricing.taxRate}%`
            }
            value={formatMoney(pricing.tax)}
            muted={pricing.taxInclusive}
          />
        )}
      </dl>

      <div className="mt-4 flex items-baseline justify-between border-t border-mist pt-4">
        <span className="text-xs font-medium tracking-[0.08em] text-ink uppercase">Total</span>
        <span className="text-lg font-medium text-brand" data-numeric>
          {formatMoney(pricing.total)}
        </span>
      </div>

      {shipping?.amountToFreeShipping > 0 && (
        <p className="mt-3 text-2xs text-muted">
          Add {formatMoney(shipping.amountToFreeShipping)} for free delivery.
        </p>
      )}

      {shipping?.freeReason === 'threshold' && (
        <p className="mt-3 text-2xs text-success">Free delivery applied.</p>
      )}

      {children}
    </div>
  );
};

const Row = ({ label, value, tone, muted }) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-xs text-muted">{label}</dt>
    <dd
      className={cn(
        'text-right text-xs',
        tone === 'success' ? 'text-success' : muted ? 'text-muted' : 'text-ink',
      )}
      data-numeric
    >
      {value}
    </dd>
  </div>
);

export default OrderSummary;
